/**
 * ReferralCard Component
 *
 * Card for displaying referral code and sharing functionality.
 * Also shows referral stats and pending rewards.
 */

import React, { useState } from 'react';
import { View, Text, Pressable, StyleSheet, Share, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import {
  Gift,
  Copy,
  Share2,
  Users,
  Check,
  ChevronDown,
  ChevronUp,
  Sparkles,
  TrendingUp,
} from 'lucide-react-native';
import Animated, { FadeIn, FadeInUp, Layout } from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';
import * as Clipboard from 'expo-clipboard';

import AnimatedPressable from '@/components/AnimatedPressable';
import { useThemeColors } from '@/lib/useThemeColors';
import { useReferralStore, REFERRAL_DISCOUNT_PERCENT, generateReferralLink } from '@/lib/referral';
import { formatPrice } from '@/lib/subscription/pricing';

// ============================================================================
// Types
// ============================================================================

interface ReferralCardProps {
  userId: string;
  userType: 'startup' | 'investor';
  variant?: 'default' | 'compact';
}

// ============================================================================
// Component
// ============================================================================

export function ReferralCard({ userId, userType, variant = 'default' }: ReferralCardProps) {
  const colors = useThemeColors();
  const [showStats, setShowStats] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateMyCode = useReferralStore((s) => s.generateMyCode);
  const myReferralCode = useReferralStore((s) => s.myReferralCode);
  const getStats = useReferralStore((s) => s.getStats);
  const getActiveReward = useReferralStore((s) => s.getActiveReward);

  // Ensure user has a referral code
  const referralCode = myReferralCode || generateMyCode(userId, userType);
  const stats = getStats();
  const activeReward = getActiveReward();

  const handleCopyCode = async () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    await Clipboard.setStringAsync(referralCode.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);

    const referralLink = generateReferralLink(referralCode.code, '/signup');
    const message =
      userType === 'startup'
        ? `I'm using Inverio to improve my investment readiness! Get ${REFERRAL_DISCOUNT_PERCENT}% off your first month with my link: ${referralLink}`
        : `Join me on Inverio to discover pre-vetted startup opportunities! Get ${REFERRAL_DISCOUNT_PERCENT}% off your first month with my link: ${referralLink}`;

    try {
      await Share.share({
        message,
        title: `Inverio Referral - ${REFERRAL_DISCOUNT_PERCENT}% Off`,
      });
    } catch (error) {
      // Share cancelled or failed
    }
  };

  // Compact variant
  if (variant === 'compact') {
    return (
      <AnimatedPressable onPress={handleShare}>
        <View
          style={[
            styles.compactContainer,
            { backgroundColor: colors.primary[50], borderColor: colors.primary[200] },
          ]}
        >
          <View style={styles.compactLeft}>
            <Gift size={18} color={colors.primary[600]} />
            <View>
              <Text style={[styles.compactTitle, { color: colors.text.primary }]}>
                Refer & Save {REFERRAL_DISCOUNT_PERCENT}%
              </Text>
              <Text style={[styles.compactCode, { color: colors.primary[600] }]}>
                {referralCode.code}
              </Text>
            </View>
          </View>
          <Share2 size={18} color={colors.primary[500]} />
        </View>
      </AnimatedPressable>
    );
  }

  // Default variant
  return (
    <Animated.View entering={FadeIn.duration(400)} layout={Layout.springify()}>
      <View
        style={[
          styles.container,
          { backgroundColor: colors.surface.primary, borderColor: colors.border.light },
        ]}
      >
        {/* Header Gradient */}
        <LinearGradient
          colors={[colors.primary[500], colors.primary[600]]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={styles.header}
        >
          <View style={styles.headerContent}>
            <View style={styles.headerLeft}>
              <View style={styles.headerIcon}>
                <Gift size={20} color="#fff" />
              </View>
              <View>
                <Text style={styles.headerTitle}>Refer & Earn</Text>
                <Text style={styles.headerSubtitle}>
                  {REFERRAL_DISCOUNT_PERCENT}% off for you & your friend
                </Text>
              </View>
            </View>
          </View>
        </LinearGradient>

        {/* Code Display */}
        <View style={styles.codeSection}>
          <Text style={[styles.codeLabel, { color: colors.text.tertiary }]}>
            Your Referral Code
          </Text>
          <View style={[styles.codeBox, { backgroundColor: colors.surface.secondary }]}>
            <Text style={[styles.codeText, { color: colors.text.primary }]}>
              {referralCode.code}
            </Text>
            <AnimatedPressable
              onPress={handleCopyCode}
              style={[
                styles.copyButton,
                { backgroundColor: copied ? colors.traffic.greenLight : colors.primary[100] },
              ]}
            >
              {copied ? (
                <Check size={18} color={colors.success} />
              ) : (
                <Copy size={18} color={colors.primary[600]} />
              )}
            </AnimatedPressable>
          </View>
        </View>

        {/* Actions */}
        <View style={styles.actions}>
          <AnimatedPressable
            onPress={handleShare}
            style={[
              styles.shareButton,
              { backgroundColor: colors.primary[500] },
            ]}
          >
            <Share2 size={18} color="#fff" />
            <Text style={styles.shareButtonText}>Share with Friends</Text>
          </AnimatedPressable>
        </View>

        {/* Stats Toggle */}
        <AnimatedPressable
          onPress={() => setShowStats(!showStats)}
          style={[styles.statsToggle, { borderTopColor: colors.border.light }]}
        >
          <View style={styles.statsToggleLeft}>
            <Users size={16} color={colors.text.tertiary} />
            <Text style={[styles.statsToggleText, { color: colors.text.secondary }]}>
              {stats.totalReferrals} referral{stats.totalReferrals !== 1 ? 's' : ''}
              {stats.convertedReferrals > 0 && ` • ${stats.convertedReferrals} converted`}
            </Text>
          </View>
          {showStats ? (
            <ChevronUp size={18} color={colors.text.tertiary} />
          ) : (
            <ChevronDown size={18} color={colors.text.tertiary} />
          )}
        </AnimatedPressable>

        {/* Expanded Stats */}
        {showStats && (
          <Animated.View entering={FadeInUp.duration(300)} style={styles.statsExpanded}>
            <View style={styles.statsGrid}>
              <View style={[styles.statBox, { backgroundColor: colors.surface.secondary }]}>
                <Text style={[styles.statValue, { color: colors.text.primary }]}>
                  {stats.pendingReferrals}
                </Text>
                <Text style={[styles.statLabel, { color: colors.text.tertiary }]}>
                  Pending
                </Text>
              </View>
              <View style={[styles.statBox, { backgroundColor: colors.traffic.greenLight }]}>
                <Text style={[styles.statValue, { color: colors.success }]}>
                  {stats.convertedReferrals}
                </Text>
                <Text style={[styles.statLabel, { color: colors.text.tertiary }]}>
                  Converted
                </Text>
              </View>
              <View style={[styles.statBox, { backgroundColor: colors.primary[50] }]}>
                <Text style={[styles.statValue, { color: colors.primary[600] }]}>
                  {stats.rewardsPending}
                </Text>
                <Text style={[styles.statLabel, { color: colors.text.tertiary }]}>
                  Rewards
                </Text>
              </View>
            </View>

            {/* Active Reward Banner */}
            {activeReward && (
              <View style={[styles.rewardBanner, { backgroundColor: colors.traffic.greenLight }]}>
                <Sparkles size={16} color={colors.success} />
                <Text style={[styles.rewardText, { color: colors.success }]}>
                  You have a {activeReward.value}% discount to use!
                </Text>
              </View>
            )}
          </Animated.View>
        )}
      </View>
    </Animated.View>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = StyleSheet.create({
  container: {
    borderRadius: 20,
    overflow: 'hidden',
    borderWidth: 1,
  },
  header: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#fff',
  },
  headerSubtitle: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.8)',
    marginTop: 2,
  },
  codeSection: {
    paddingHorizontal: 20,
    paddingTop: 20,
    paddingBottom: 16,
  },
  codeLabel: {
    fontSize: 12,
    fontWeight: '500',
    marginBottom: 8,
  },
  codeBox: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    padding: 4,
    paddingLeft: 16,
  },
  codeText: {
    flex: 1,
    fontSize: 20,
    fontWeight: '700',
    letterSpacing: 2,
  },
  copyButton: {
    width: 44,
    height: 44,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actions: {
    paddingHorizontal: 20,
    paddingBottom: 16,
  },
  shareButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 12,
    gap: 8,
  },
  shareButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#fff',
  },
  statsToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderTopWidth: 1,
  },
  statsToggleLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  statsToggleText: {
    fontSize: 13,
  },
  statsExpanded: {
    paddingHorizontal: 20,
    paddingBottom: 16,
  },
  statsGrid: {
    flexDirection: 'row',
    gap: 10,
  },
  statBox: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 12,
    alignItems: 'center',
  },
  statValue: {
    fontSize: 20,
    fontWeight: '700',
  },
  statLabel: {
    fontSize: 11,
    marginTop: 2,
  },
  rewardBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 12,
    paddingVertical: 10,
    borderRadius: 10,
    gap: 8,
  },
  rewardText: {
    fontSize: 13,
    fontWeight: '600',
  },

  // Compact styles
  compactContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderRadius: 14,
    borderWidth: 1,
  },
  compactLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  compactTitle: {
    fontSize: 13,
    fontWeight: '600',
  },
  compactCode: {
    fontSize: 12,
    fontWeight: '500',
    marginTop: 2,
  },
});
